import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button.jsx'; // shadcn button
import { useToast } from "../../hooks/use-toast.js";
import axios from "../../lib/axios.js";
import { PlayIcon, PauseIcon, Loader2 } from 'lucide-react';

const CallRecording = ({ id, fileName }) => {
    const [recordingUrl, setRecordingUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const { toast } = useToast();
    const audioRef = useRef(null);

    const handlePlay = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsLoading(true);
        try {
            const response = await axios.get(`/calls/${id}/recording`);
            if (response.data.recording_url) {
                setRecordingUrl(response.data.recording_url);
            }
            setIsPlaying(true);
        } catch (error) {
            console.error('Error fetching MP3 URL:', error);
            toast({
                title: "Error",
                description: "Failed to load the MP3 file.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const togglePlayback = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handlePause);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handlePause);
        };
    }, []);

    // Auto-play once recordingUrl is set
    useEffect(() => {
        if (recordingUrl && audioRef.current) {
            audioRef.current.play().catch((err) => {
                console.warn("Autoplay blocked or failed:", err);
            });
        }
    }, [recordingUrl]);

    if (!fileName) return null;

    return (
        <div className="flex items-center space-x-4 mt-4">
            {!recordingUrl ? (
                <Button
                    variant="ghost"
                    disabled={isLoading}
                    onClick={handlePlay}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"
                >
                    {isLoading ? (
                        <Loader2 className="animate-spin w-5 h-5 text-muted-foreground" />
                    ) : (
                        <PlayIcon className="h-6 w-6" />
                    )}
                </Button>
            ) : (
                <>
                    <Button
                        variant="ghost"
                        onClick={togglePlayback}
                        className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600"
                    >
                        {isPlaying ? (
                            <PauseIcon className="h-6 w-6" />
                        ) : (
                            <PlayIcon className="h-6 w-6" />
                        )}
                    </Button>
                    <audio ref={audioRef} src={recordingUrl} preload="auto" />
                </>
            )}
        </div>
    );
};

export default CallRecording;
